import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, CircularProgress, Alert,
   TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Collapse, Card, CardContent,Chip, Grid, Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';  
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'; 

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);   
dayjs.extend(isSameOrBefore);

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
    minWidth: 0,
    padding: '2px 8px',
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    borderRadius: theme.shape.borderRadius,
}));

const StyledTableRow = styled(TableRow)(({ theme, index }) => ({
    backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff',
    '& > td': {
        borderBottom: 'none',
    },
}));

const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();
  
  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const DetailSection = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "0.9rem", color: "#11224d", mb: 0.5, borderBottom: '1px solid #ddd', pb: 0.5 }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const getSelectionKey = (post) => `${post.postType === 'carrier' ? 'truck' : 'load'}-${post.id}`;

const PostRow = ({ post, type, onToggleActive, onDelete, selectionMode, isSelected, onSelect }) => {
    const [open, setOpen] = useState(false);

    const isLoad = type === 'shipper' || type === 'broker';
    const details = post.details || {};

    return (
        <>
            <StyledTableRow hover onClick={() => {
                if (selectionMode) {
                    onSelect(post);
                } else {
                    setOpen(!open);
                }
            }} sx={{ cursor: 'pointer' }}>
                <TableCell padding="checkbox">
                    {selectionMode ? (
                        <Checkbox checked={isSelected} onClick={(e) => e.stopPropagation()} onChange={() => onSelect(post)} />
                    ) : (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
                    )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{dayjs(post.timePosted).tz('America/New_York').format('MM/DD/YY hh:mm A')}</TableCell>
               <TableCell sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
    <Chip
        label={type}
        size="small"
        sx={{
            fontSize: '0.65rem',
            height: '20px', 
            backgroundColor: 
                type === 'shipper' ? '#c8e6c9' : 
                type === 'carrier' ? '#fff9c4' : 
                type === 'broker'  ? '#bbdefb' : '#e0e0e0', 
            color: 
                type === 'shipper' ? '#1b5e20' : 
                type === 'carrier' ? '#f57f17' : 
                type === 'broker'  ? '#0d47a1' : '#000000', 
        }}
    />
    
</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{post.origin || 'N/A'}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{post.companyName || details.shipper_company || 'N/A'}</TableCell>
                <TableCell align="center">
                    {(() => {
                        let statusLabel;
                        let statusColor;
                        const postDate = dayjs(post.pickupDate || post.availableDate);
                        const isPastDate = postDate.isBefore(dayjs().startOf('day'));

                        if (post.isActive) {
                            if (isPastDate) {
                                statusLabel = 'Past Due';
                                statusColor = 'default'; 
                            } else {
                                statusLabel = 'Active';
                                statusColor = 'success';
                            }
                        } else {
                            statusLabel = 'Disabled';
                            statusColor = 'error';
                        }
                        return <Chip label={statusLabel} size="small" color={statusColor} sx={{ fontSize: '0.65rem', height: '20px' }} />;
                    })()}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <ActionButton variant="outlined" size="small" color={post.isActive ? 'warning' : 'success'} onClick={() => onToggleActive(post, type)}>
                            {post.isActive ? 'Disable' : 'Enable'}
                        </ActionButton>
                        {!selectionMode && (
                            <IconButton size="small" color="error" onClick={() => onDelete(post)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </TableCell>
            </StyledTableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Card sx={{ m: 1, border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <CardContent>
                                <Grid container spacing={2}>
                                   
                                    <Grid item xs={12} md={4}>
                                        <DetailSection title="Post Details">
                                            <Typography variant="caption" display="block"><b>Origin:</b> {post.origin || 'N/A'}</Typography>
                                            <Typography variant="caption" display="block"><b>Destination:</b> {post.destination || 'N/A'}</Typography>
                                            <Typography variant="caption" display="block"><b>Equipment:</b> {post.equipment || post.truckType || 'N/A'}</Typography>
                                            <Typography variant="caption" display="block"><b>Feet:</b> {post.length ? `${post.length} ft` : 'N/A'}</Typography>
                                            <Typography variant="caption" display="block"><b>Weight:</b> {post.weight ? `${post.weight} lbs` : 'N/A'}</Typography>
                                            {isLoad && <Typography variant="caption" display="block"><b>Commodity:</b> {post.commodity || 'N/A'}</Typography>}
                                            <Typography variant="caption" display="block"><b>Reference ID:</b> {post.referenceId || 'N/A'}</Typography>  
                                            <Typography variant="caption" display="block"><b>Posted:</b> {dayjs(post.timePosted).tz('America/New_York').format('MM/DD/YYYY hh:mm A')}</Typography>
                                        </DetailSection>
                                    </Grid>

                                   
                                    <Grid item xs={12} md={4}>
                                        <DetailSection title="Company Info">
                                            <Typography variant="caption" display="block"><b>Company:</b> {post.companyName || details.shipper_company || 'N/A'}</Typography>
                                            {isLoad ? (
                                                <>
                                                    <Typography variant="caption" display="block"><b>Address:</b> {details.shipper_address || 'N/A'}</Typography>
                                                    <Typography variant="caption" display="block"><b>Contact:</b> {details.shipper_phone || 'N/A'}</Typography>
                                                    <Typography variant="caption" display="block"><b>Email:</b> {details.shipper_email || 'N/A'}</Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="caption" display="block"><b>Address:</b> {post.companyAddress || 'N/A'}</Typography>
                                                    <Typography variant="caption" display="block"><b>Contact:</b> {post.contactInfo || 'N/A'}</Typography>
                                                    <Typography variant="caption" display="block"><b>Email:</b> {post.carrierEmail || 'N/A'}</Typography>
                                                </>
                                            )}
                                          {type !== 'shipper' && (
    <>
        <Typography variant="caption" display="block">
            <b>DOT Number:</b> {post.dot_number || 'N/A'}
        </Typography>
        {post.mc_number && post.mc_number !== 'MC-' && (
            <Typography variant="caption" display="block">
                <b>MC Number:</b> {post.mc_number}
            </Typography>
        )}
    </>
)}
                                            {!isLoad && <Typography variant="caption" display="block"><b>Base State:</b> {post.companyState || 'N/A'}</Typography>}
                                        </DetailSection>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        {isLoad && (
                                            <DetailSection title="Rate & Payment">
                                                <Typography variant="caption" display="block"><b>Offer Rate ({post.rateType || 'N/A'}):</b> {post.offerRate ? `$${Number(post.offerRate).toFixed(2)}` : "-"}</Typography>
                                                <Typography variant="caption" display="block">
                                                    <b>Rate / Mile:</b> {post.tripMiles && post.offerRate ? (post.rateType === "Flat" ? `$${(Number(post.offerRate) / Number(post.tripMiles)).toFixed(2)}` : `$${Number(post.offerRate).toFixed(2)}`) : "-"}
                                                </Typography>
                                                <Typography variant="caption" display="block"><b>Trip Miles:</b> {post.tripMiles ? `${Math.round(Number(post.tripMiles))} mi` : "N/A"}</Typography>
                                                <Typography variant="caption" display="block">
                                                    <b>Total Rate:</b> {post.offerRate && post.tripMiles ? (post.rateType === "Flat" ? `$${Number(post.offerRate).toFixed(2)}` : `$${(Number(post.offerRate) * Number(post.tripMiles)).toFixed(2)}`) : "-"}
                                                </Typography>
                                                {type !== 'broker' && (
                                                    <>
                                                        <Typography variant="caption" display="block"><b>Payment:</b> {post.paymentMethod || 'N/A'}</Typography>
                                                        {post.paymentMethod === "Custom Terms" && (
                                                            <Typography variant="caption" display="block"><b>Terms:</b> {post.customTermsDetails || 'N/A'}</Typography>
                                                        )}
                                                    </>
                                                )}
                                            </DetailSection>
                                        )}
                                        <DetailSection title="Notes">
                                            <Typography variant="caption">
                                                {post.comment || post.comments || "No additional comments provided."}
                                            </Typography>
                                        </DetailSection>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const LAndTMonitoring = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ origin: '', company: '', startDate: null, endDate: null });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: () => {} });
    const [selectionMode, setSelectionMode] = useState(false);
    const [selected, setSelected] = useState([]);

 const fetchAllPosts = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('jwtToken');
            const headers = { Authorization: `Bearer ${token}` };

        
            const loadsPromise = axios.get(`${API_BASE_URL}/api/admin/loads`, { headers });
 const trucksPromise = axios.get(`${API_BASE_URL}/api/admin/trucks`, { headers });

            const [loadsRes, trucksRes] = await Promise.all([loadsPromise, trucksPromise]);

           
            const allLoads = (loadsRes.data.results || []).map(p => ({ 
                ...p, 
                postType: p.posted_by_role || 'shipper' 
            }));
           
            const allTrucks = (trucksRes.data.results || []).map(p => ({ 
                ...p, 
                postType: 'carrier' 
            }));

            const combined = [...allLoads, ...allTrucks].sort((a, b) => dayjs(b.timePosted).valueOf() - dayjs(a.timePosted).valueOf());
            setPosts(combined);
        } catch (err) {
            setError(err.message || 'Failed to fetch posts.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllPosts();
 }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };
    const handleToggleActive = async (post, type) => {
        const endpoint = (type === 'shipper' || type === 'broker') ? 'loads' : 'trucks';
        const token = localStorage.getItem('jwtToken');
        try {
            await axios.patch(`${API_BASE_URL}/api/admin/${endpoint}/${post.id}/status`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchAllPosts();
        } catch (err) {
            setError(`Failed to update status for ${type} #${post.id}`);
        }
    };

    const handleEnterSelectionMode = (post) => {
        setSelectionMode(true);
        setSelected([getSelectionKey(post)]);
    };

    const handleExitSelectionMode = () => {
        setSelectionMode(false);
        setSelected([]);
    };

    const handleSelect = (post) => {
        const key = getSelectionKey(post);
        const selectedIndex = selected.indexOf(key);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, key);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleDeleteRequest = (keys) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete ${keys.length > 1 ? `these ${keys.length} items` : 'this item'}? This action is permanent.`,
            onConfirm: () => handleConfirmBulkDelete(keys)
        });
    };

    const handleConfirmBulkDelete = async (keys) => {
        const token = localStorage.getItem('jwtToken');
        const headers = { Authorization: `Bearer ${token}` };
        
        const promises = keys.map(key => {
            const [type, id] = key.split('-');
            const endpoint = type === 'truck' ? 'trucks' : 'loads';
            return axios.delete(`${API_BASE_URL}/api/admin/${endpoint}/${id}`, { headers });
        });

        try {
            await Promise.all(promises);
            fetchAllPosts();
            handleExitSelectionMode();
        } catch (err) {
            setError('Failed to delete some items.');
            console.error(err);
        } finally {
             setConfirmDialog({ open: false, onConfirm: () => {} });
        }
    };

    const filteredPosts = useMemo(() => {
    return posts.filter(post => {
        const originMatch = !filters.origin || (post.origin && post.origin.toLowerCase().includes(filters.origin.toLowerCase()));
        const companyMatch = !filters.company || ((post.companyName || post.details?.shipper_company) && (post.companyName || post.details.shipper_company).toLowerCase().includes(filters.company.toLowerCase()));

       
        const postDate = dayjs(post.timePosted);
        const startMatch = !filters.startDate || postDate.isSameOrAfter(dayjs(filters.startDate), 'day');
        const endMatch = !filters.endDate || postDate.isSameOrBefore(dayjs(filters.endDate), 'day');
        const dateMatch = startMatch && endMatch;
   

        return originMatch && companyMatch && dateMatch;
    });
}, [posts, filters]);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = filteredPosts.map((p) => getSelectionKey(p));
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <StyledPaper>
                
                <Grid container spacing={2} alignItems="center">
    <Grid item xs={12} sm={3}>
        <TextField fullWidth size="small" label="Filter by Origin" name="origin" value={filters.origin} onChange={handleFilterChange} />
    </Grid>
    <Grid item xs={12} sm={3}>
        <TextField fullWidth size="small" label="Filter by Company" name="company" value={filters.company} onChange={handleFilterChange} />
    </Grid>
    <Grid item xs={12} sm={3}>
        <TextField
            fullWidth
            size="small"
            label="Start Date"
            name="startDate"
            type="date"
            value={filters.startDate || ''}
            onChange={handleFilterChange}
            InputLabelProps={{ shrink: true }}
        />
    </Grid>
    <Grid item xs={12} sm={3}>
        <TextField
            fullWidth
            size="small"
            label="End Date"
            name="endDate"
            type="date"
            value={filters.endDate || ''}
            onChange={handleFilterChange}
            InputLabelProps={{ shrink: true }}
        />
    </Grid>
</Grid>
            </StyledPaper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#11224d' }}>
                    Monitoring ({filteredPosts.length})
                </Typography>
                {selectionMode ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteRequest(selected)}
                            disabled={selected.length === 0}
                        >
                            Delete ({selected.length})
                        </Button>
                        <Button 
                            size="small" 
                            onClick={handleExitSelectionMode}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                    </Box>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectionMode(true)}
                        disabled={filteredPosts.length === 0}
                    >
                        Select
                    </Button>
                )}
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 140px)' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ backgroundColor: '#11224d' }}>
                                {selectionMode && (
                                    <Checkbox 
                                        sx={{ color: 'white' }}
                                        indeterminate={selected.length > 0 && selected.length < filteredPosts.length}
                                        checked={filteredPosts.length > 0 && selected.length === filteredPosts.length}
                                        onChange={handleSelectAll}
                                    />
                                )}
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: '#11224d' }}>Posted</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: '#11224d' }}>Type</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: '#11224d' }}>Origin</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: '#11224d' }}>Company</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', backgroundColor: '#11224d' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right', backgroundColor: '#11224d' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPosts.map((post, idx) => (
                            <PostRow
                                key={`${post.postType}-${post.id}`}
                                post={post}
                                type={post.postType}
                                idx={idx}
                                onToggleActive={handleToggleActive}
                                onDelete={handleEnterSelectionMode}
                                selectionMode={selectionMode}
                                isSelected={selected.includes(getSelectionKey(post))}
                                onSelect={handleSelect}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
                <DialogTitle>{confirmDialog.title}</DialogTitle>
                <DialogContent>
                    <Typography>{confirmDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
                    <Button onClick={confirmDialog.onConfirm} color="error" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LAndTMonitoring;